import { useState, useEffect, useCallback } from 'react';
import UpdateAnnonceForm from "../../../components/update/update-annonce-form"; 
import { useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../../cors/Loading'; 

export default function Page() {
  const { token } = useAuth();
  const { id } = useParams();
  const [annonce, setAnnonce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnnonce = useCallback(async () => {
    if (!id) {
      setError("L'ID de l'annonce est manquant."); 
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);   
    try {
      if (!token) {
        toast.error("Vous n'êtes pas authentifié. Veuillez vous connecter.");
        setError("Jeton d'authentification introuvable. Veuillez vous connecter.");
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/annonces/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `Échec de la récupération de l'annonce : ${res.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await res.json();
      
      // Handle different response formats
      if (data.annonce) {
        setAnnonce(data.annonce);
      } else if (data.data) {
        setAnnonce(data.data);
      } else {
        setAnnonce(data);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération de l'annonce :", err);
      setError(err.message || "Une erreur inattendue est survenue lors de la récupération de l'annonce.");
      toast.error(`Erreur : ${err.message || "Échec du chargement de l'annonce."}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAnnonce();
  }, [fetchAnnonce]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold text-red-700 mb-4">Erreur lors du chargement de l'annonce</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchAnnonce} 
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            Réessayer 
          </button>
        </div>
      </div>
    );
  }

  if (!annonce) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Annonce Introuvable</h2>
          <p className="text-gray-600">L'annonce avec l'ID "{id}" n'a pas pu être chargée ou n'existe pas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            Modifier l'Annonce
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            Modifiez les détails de cette annonce spécifique.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <UpdateAnnonceForm annonce={annonce} />
        </div>
      </div>
    </div>
  );
}